import { useCallback, useEffect, useRef, useState } from "react";
import { useSelector, useDispatch } from "react-redux";
import { Input, Button, translateMessage } from "@delibrandev/utils";
import { FaRegTrashAlt } from "react-icons/fa";
import { TbPlus } from "react-icons/tb";
import "./style.css";
import { addSpecialClosedDay, editSpecialClosedDay, getSpecialClosedDays, removeSpecialClosedDay, updateBusinessHours } from "../../api/configuration";
import { setAlert } from "../../reducers/userConfigSlice";
import { DAYS, scheduleDraft, schedulePayload } from "./weeklySchedule";

export const ScheduleEditor = ({ schedule, refresh }) => {
  const channels = useSelector(state => state.aplicationConfig.corporationData.channels) || [];
  const [draft, setDraft] = useState(() => scheduleDraft(schedule));
  const [saving, setSaving] = useState(false);
  const [message, setMessage] = useState("");
  const [error, setError] = useState("");
  const inFlight = useRef(false);
  if (!schedule?.businessHoursId) return null;
  const change = (key, value) => { setDraft(previous => ({ ...previous, [key]: value })); setMessage(""); setError(""); };
  const changeDay = (day, key, value) => { setDraft(previous => ({ ...previous, dayInfo: { ...previous.dayInfo, [day]: { ...previous.dayInfo[day], [key]: value } } })); setMessage(""); setError(""); };
  const save = async event => {
    event.preventDefault();
    if (inFlight.current) return;
    let payload;
    try { payload = schedulePayload(draft); } catch (failure) { setError(failure.message); return; }
    inFlight.current = true; setSaving(true); setError(""); setMessage("");
    try {
      const response = await updateBusinessHours(payload, schedule.businessHoursId);
      if (response?.error !== false) throw new Error("No se ha podido guardar el horario. Revisa tu conexión o acceso y vuelve a intentarlo.");
      setMessage("Horario guardado correctamente.");
      await refresh?.(schedule.businessHoursId);
    } catch (failure) { setError(failure.message || "No se ha podido guardar el horario. Vuelve a intentarlo."); }
    finally { inFlight.current = false; setSaving(false); }
  };
  return <>
    <form className="schedule-form" onSubmit={save} aria-busy={saving}>
      <p>Configura las franjas de cada día y pulsa Guardar horario. Los cambios no se aplican hasta guardar. Las horas pueden incluir minutos, por ejemplo 09:15.</p>
      <fieldset disabled={saving}>
        <legend className="visually-hidden">Configuración del horario</legend>
        <div className="schedule-general">
          <label>Nombre<input required value={draft.name} onChange={e => change("name", e.target.value)} /></label>
          <label>Tipo<select value={draft.type} onChange={e => change("type", e.target.value)}><option value="DEFAULT">General</option><option value="CUSTOM">Personalizado</option></select></label>
          <label>Antelación del último pedido (minutos)<input type="number" min="1" max="60" required value={draft.lastOrderTime} onChange={e => change("lastOrderTime", e.target.value)} /></label>
          <label>Distribución<select value={draft.isContinuous ? "continuous" : "split"} onChange={e => change("isContinuous", e.target.value === "continuous")}><option value="continuous">Horario continuo</option><option value="split">Horario partido</option></select></label>
          <label>Canal de venta asociado<select value={draft.salesChannelId} onChange={e => change("salesChannelId", e.target.value)}><option value="">Sin canal asociado</option>{channels.map(channel => <option key={channel.id || channel.salesChannelId} value={channel.id || channel.salesChannelId}>{channel.name}</option>)}</select></label>
        </div>
        <p>{draft.isContinuous ? "Una franja por día: apertura y cierre." : "Dos franjas por día: apertura → cierre de mañana y reapertura → cierre final."} Un cierre anterior a la apertura corresponde al día siguiente.</p>
        {DAYS.map(([day, label]) => <fieldset className="schedule-day" key={day}>
          <legend>{label}</legend>
          <label className="schedule-closed"><input type="checkbox" checked={draft.dayInfo[day].isClosed} onChange={e => changeDay(day, "isClosed", e.target.checked)} />Cerrado {label}</label>
          <div className="schedule-times">
            {(draft.isContinuous ? [["openingTime", "Apertura"], ["closingTime", "Cierre"]] : [["openingTime", "Apertura"], ["splitClosingTime", "Cierre de mañana"], ["splitOpeningTime", "Reapertura"], ["closingTime", "Cierre final"]]).map(([field, name]) => <label key={field}>{name}<input aria-label={`${label}: ${name}`} type="time" step="60" required={!draft.dayInfo[day].isClosed} disabled={draft.dayInfo[day].isClosed} value={draft.dayInfo[day][field]} onChange={e => changeDay(day, field, e.target.value)} /></label>)}
          </div>
        </fieldset>)}
        <button type="submit" className="schedule-save">{saving ? "Guardando…" : "Guardar horario"}</button>
      </fieldset>
      {error && <p role="alert">{error}</p>}{message && <p role="status">{message}</p>}
    </form>
    <hr /><SpecialDays />
  </>;
};

const SpecialDays = () => {
    const dispatch = useDispatch();
    const [specialDays, updateSpecialDays] = useState([]);

    const addSpecialClosed = async () => {
      const today = new Date().toISOString().split("T")[0];
      const payload = {
        date: today,
      };
      console.log(payload);
      const response = await addSpecialClosedDay(payload);
      if (response && !response.error) {
        dispatch(setAlert(translateMessage(response?.message || "Bad request")));
        console.log(response);
        querySpecialClosedDays();
      } else {
        dispatch(setAlert(translateMessage(response?.message || "Bad request")));
        console.log("Error en la operación");
      }
    };

    const editSpecialClosed = async (date, id) => {
      const payload = {
        date: date,
      };
      const response = await editSpecialClosedDay(payload, id);
      if (response && !response.error) {
        dispatch(setAlert(translateMessage(response?.message || "Bad request")));
        console.log(response);
        querySpecialClosedDays();
      } else {
        dispatch(setAlert(translateMessage(response?.message || "Bad request")));
        console.log("Error en la operación");
      }
    };

    const removeSpecialClosed = async (id) => {
      const payload = {};
      const response = await removeSpecialClosedDay(payload, id);
      if (response && !response.error) {
        dispatch(setAlert(translateMessage(response?.message || "Bad request")));
        console.log(response);
        querySpecialClosedDays();
      } else {
        dispatch(setAlert(translateMessage(response?.message || "Bad request")));
        console.log("Error en la operación");
      }
    };

    const querySpecialClosedDays = useCallback(async () => {
      const response = await getSpecialClosedDays();
      if (response && !response.error) {
        updateSpecialDays(response.data);
      } else {
        dispatch(setAlert(translateMessage(response?.message || "Bad request")));
        console.log("Error en la operación");
      }
    }, [dispatch]);

    useEffect(() => {
      querySpecialClosedDays();
    }, [querySpecialClosedDays]);

    return (
      <>
        <div className="flex mt-4">
          <div>
            <b>Dias especiales de cierre</b>
            <div className="inputDescription">Agrega los dias en los que estará cerrado</div>
          </div>
          <div>
            <Button text={"Agregar fecha"} icon={<TbPlus />} customClass={"customButtonColor2"} action={addSpecialClosed} />
          </div>
        </div>

        <div className="row mt-4">
          {specialDays &&
            specialDays.length > 0 &&
            specialDays.map((day) => (
              <div className="col-md-3" key={day.specialClosedDaysId}>
                <div className="flex-gap closedSpecialDay">
                  <Input
                    id={"idInput" + day.specialClosedDaysId}
                    type={"date"}
                    placeholder=""
                    icon={null}
                    classname={"w-100"}
                    iconPositionRight={true}
                    label=""
                    description=""
                    defaultValue={day.date}
                    onChange={(value) => editSpecialClosed(value, day.specialClosedDaysId)}
                  />
                  <Button
                    text={""}
                    icon={<FaRegTrashAlt />}
                    customClass={"customButtonColor1"}
                    action={() => removeSpecialClosed(day.specialClosedDaysId)}
                  />
                </div>
              </div>
            ))}
        </div>
      </>
    );
  };
